/**
 * sectionAdapters — Property → 각 섹션 컴포넌트 props 변환 순수 함수.
 *
 * 정책:
 *   - fetch 없음. property 는 인자로 주입.
 *   - 데이터가 없으면 빈 배열/null 을 반환한다. (SectionRenderer 가 빈 섹션을 skip)
 *   - 가격/콘텐츠 텍스트는 반드시 policy formatter 를 경유한다.
 */

import { Property, DiscountBenefit } from '../../services/api';
import { formatPriceForDisplay, formatUnits, sanitizeContentText } from './formatters';
import {
  cleanFieldText,
  cleanBuildingScale,
  formatMoveInDate,
  formatAreaRange,
} from '../../utils/formatters';
import type { PriceTableRow } from '../../design-system/components/section/PriceTable';
import type { DataRowProps } from '../../design-system/components/section/DataRow';
import type { PlanGridCell } from '../../design-system/components/section/PlanGrid';

/** 기본 정보 행 (label/value 만 사용). */
export type BasicRow = Pick<DataRowProps, 'label' | 'value'>;

/**
 * 문자열 필드만 안전하게 추출.
 * `constructor` 는 JS 예약 프로퍼티라 API 응답에 없으면 Object 생성자를 상속받는다.
 * cleanFieldText 에 함수가 넘어가는 것을 막기 위해 string 여부를 먼저 확인한다.
 */
function asStringField(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/** 요약 섹션 데이터. */
export interface SummaryData {
  status?: string;
  isPreRegistration: boolean;
  isReopen?: boolean;
  priceLabel: string;
  address: string;
  title: string;
}

/**
 * 분양가 표 행 목록.
 *   - type_prices 가 있으면 타입별 행 (층 정보 없으므로 type_name 을 층 자리에 사용)
 *   - 없으면 min/max_price 폴백 단일 행
 *   - 둘 다 없으면 빈 배열
 */
export function buildPriceRows(property: Property): PriceTableRow[] {
  const typePrices = property.type_prices;
  if (typePrices && typePrices.length > 0) {
    return typePrices
      .filter((tp) => typeof tp.top_price === 'number' && tp.top_price! > 0)
      .map((tp) => ({
        floor: tp.type_name,
        supplyCount: tp.supply_count ?? null,
        // 공고문 최저가가 확정된 평형은 '최저 ~ 최고', 아니면 최고가만 (2026-10-01)
        price: formatPriceForDisplay(tp.min_price ?? tp.top_price, tp.top_price),
      }));
  }

  const hasFallback =
    (typeof property.min_price === 'number' && property.min_price > 0) ||
    (typeof property.max_price === 'number' && property.max_price > 0);
  if (hasFallback) {
    return [
      {
        floor: property.title,
        supplyCount: null,
        price: formatPriceForDisplay(property.min_price, property.max_price),
      },
    ];
  }

  return [];
}

/**
 * 기본 정보 행 목록 — 값이 있는 행만 포함한다.
 * 순서: 규모 · 세대수 · 주차 · 시공사 · 시행사 · 입주예정.
 */
export function buildBasicRows(property: Property): BasicRow[] {
  const rows: BasicRow[] = [];

  const scale = cleanBuildingScale(property.building_scale);
  if (scale) rows.push({ label: '규모', value: scale });

  if (typeof property.total_units === 'number' && property.total_units > 0) {
    rows.push({ label: '세대수', value: formatUnits(property) });
  }

  if (typeof property.parking_spaces === 'number' && property.parking_spaces > 0) {
    rows.push({ label: '주차', value: `${property.parking_spaces.toLocaleString()}대` });
  }

  const constructorName = cleanFieldText(asStringField(property.constructor));
  if (constructorName) rows.push({ label: '시공사', value: constructorName });

  const developer = cleanFieldText(asStringField(property.developer));
  if (developer) rows.push({ label: '시행사', value: developer });

  const moveIn = formatMoveInDate(property.move_in_date);
  if (moveIn) rows.push({ label: '입주예정', value: moveIn });

  return rows;
}

/**
 * 평면 구성 셀 목록.
 *   - floor_plans 가 있으면 첫 타입의 전용면적/유형 셀 (방·욕실 데이터는 없음)
 *   - 없고 면적만 있으면 전용면적 셀
 *   - 둘 다 없으면 빈 배열
 */
export function buildPlanCells(property: Property): PlanGridCell[] {
  const plans = property.floor_plans;
  if (plans && plans.length > 0) {
    const fp = plans[0];
    const cells: PlanGridCell[] = [];
    if (typeof fp.area === 'number' && fp.area > 0) {
      cells.push({ label: '전용면적', value: `${fp.area}㎡` });
    }
    const typeName = cleanFieldText(fp.type_name);
    if (typeName) cells.push({ label: '유형', value: typeName });
    return cells;
  }

  const areaRange = formatAreaRange(property);
  if (areaRange) {
    return [{ label: '전용면적', value: areaRange }];
  }

  return [];
}

/** 오늘 날짜를 로컬 기준 YYYY-MM-DD 로 반환한다. (UTC 변환 시 KST 새벽에 하루 밀리는 것 방지) */
export function todayLocalISO(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** ISO 날짜(YYYY-MM-DD) 형식인지 검사. 뒤에 시간이 붙어도 앞 10자만 본다. */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}/;

/**
 * 혜택 노출 여부.
 *   - `is_active === false` 면 숨김
 *   - `valid_until` 이 오늘보다 이전이면 숨김 → **기간 한정 행사 자동 만료**
 *   - `valid_from` 은 판단하지 않는다. 시작 전 혜택도 '예고'로 노출하는 것이 운영 의도
 *     (예: 9/10 하루 행사를 8월부터 안내)
 *   - 값이 없거나 날짜 형식이 아니면 **노출 유지**(fail-open) — 파싱 실패로 정상 혜택이
 *     사라지는 쪽이 만료 혜택이 남는 것보다 나쁘다.
 */
export function isBenefitVisible(
  benefit: { is_active?: boolean; valid_until?: string | null },
  today: string = todayLocalISO()
): boolean {
  if (benefit.is_active === false) return false;
  const until = (benefit.valid_until ?? '').trim();
  if (!until || !ISO_DATE_RE.test(until)) return true;
  return until.slice(0, 10) >= today;
}

/**
 * 혜택 칩 목록 — discount_benefits 의 title 을 정화해 반환한다.
 * 종료일이 지난 혜택은 제외. 없으면 빈 배열.
 */
export function buildBenefit(property: Property): string[] {
  const benefits = property.discount_benefits;
  // members_only·비로그인이면 배열이 아닌 {count, login_required} — TypeError 방지
  if (!Array.isArray(benefits) || benefits.length === 0) return [];
  return benefits
    .filter((b) => isBenefitVisible(b))
    .map((b) => sanitizeContentText(b.title))
    .filter((t) => t.length > 0);
}

/** 혜택 블록 — 제목(정화) + 설명(HTML 원문, 렌더러의 CustomProse 가 정화). */
export interface BenefitBlock {
  id: number;
  title: string;
  description: string;
}

/**
 * 혜택 블록 목록 — discount_benefits 를 제목+설명 블록으로 변환한다.
 *   - is_active === false 인 항목은 제외
 *   - valid_until 이 지난 항목은 제외 (isBenefitVisible)
 *   - 제목·설명이 모두 비면 제외
 *   - title 은 이모지 제거 정화, description 은 HTML 원문(CustomProse 가 sanitize)
 * 없으면 빈 배열.
 */
export function buildBenefitBlocks(property: Property): BenefitBlock[] {
  const benefits = property.discount_benefits;
  if (!Array.isArray(benefits) || benefits.length === 0) return [];
  return benefits
    .filter((b) => isBenefitVisible(b))
    .map((b) => ({
      id: b.id,
      title: sanitizeContentText(b.title ?? ''),
      description: (b.description ?? '').trim(),
    }))
    .filter((b) => b.title.length > 0 || b.description.length > 0);
}

/**
 * 혜택안내 카드 1장 (v2i 2026-09-22 — 목업 04 상세 '혜택안내' .card).
 *   아이콘(kind) · 제목 · 우측 배지(대표혜택 | 할인율 | 금액) · 보조줄(유형 · 종류 · 설명)
 */
export interface BenefitItem {
  id: number;
  title: string;
  /** 설명 원문(HTML 가능 — 렌더러의 CustomProse 가 정화) */
  description: string;
  /** 아이콘 선택용 kind 원값(price_cut·payment·free_option·guarantee·cashback·tax·etc) */
  kind: string;
  kindLabel?: string;
  /** 주택 유형 라벨 — 유형이 2종 이상 섞였거나 분양가 인하일 때만 (아파트·오피스텔 구분) */
  unitLabel?: string | null;
  /** 할인율·금액 배지 — 할인율은 내림 표기. 대표혜택 카드는 렌더러가 '대표혜택' 배지로 대체 */
  valueBadge?: { tone: 'ylw' | 'green'; text: string } | null;
  /** 섹션 머리 날짜와 다를 때만 카드에 표시하는 종료일 ('M.D까지') */
  until?: string | null;
  untilSoon?: boolean;
}

/** '혜택안내' 섹션 뷰 모델. */
export interface BenefitView {
  items: BenefitItem[];
  /** 섹션 머리 배지 — 표시 혜택 중 가장 이른 종료일 'YYYY-MM-DD'. 없으면 null */
  untilDate: string | null;
  restricted: { count: number } | null;
}

const UNIT_ORDER = ['apt', '', 'officetel', 'commercial'];
const UNIT_LABEL: Record<string, string> = {
  apt: '아파트',
  '': '공통',
  officetel: '오피스텔',
  commercial: '상가',
};
const KIND_LABEL: Record<string, string> = {
  price_cut: '분양가 인하',
  payment: '계약·납부조건',
  free_option: '무상 제공',
  guarantee: '보장',
  cashback: '현금·상품권',
  tax: '세제',
};

function unitKey(b: DiscountBenefit): string {
  const u = b.unit_type ?? '';
  return UNIT_ORDER.includes(u) ? u : '';
}

function isNum(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/** 만원 단위 할인 금액 표기 — 서버 format_amount 와 동일 규칙(억 단위 내림, 과장 금지). */
export function formatDiscountAmount(amount: number): string {
  if (amount >= 10000) return `최대 ${Math.floor(amount / 1000) / 10}억 할인`;
  return `${amount.toLocaleString()}만원 할인`;
}

/** 할인율 배지 — 내림 표기. '-22%' / '-13~22%'. 할인율 없고 금액만 있으면 금액 배지. */
function valueBadgeOf(b: DiscountBenefit): BenefitItem['valueBadge'] {
  const max = b.discount_rate;
  const min = b.discount_rate_min;
  if (isNum(max) && max > 0) {
    const hi = Math.floor(max);
    const lo = isNum(min) ? Math.floor(min) : null;
    return { tone: 'ylw', text: lo != null && lo > 0 && lo < hi ? `-${lo}~${hi}%` : `-${hi}%` };
  }
  if (isNum(b.discount_amount) && b.discount_amount > 0) {
    return b.kind === 'price_cut'
      ? { tone: 'ylw', text: formatDiscountAmount(b.discount_amount) }
      : { tone: 'green', text: `${b.discount_amount.toLocaleString()}만 상당` };
  }
  return null;
}

/** 'YYYY-MM-DD' → 'M.D까지'. 형식이 아니면 null. */
function untilLabel(until?: string | null): string | null {
  const v = (until ?? '').trim();
  if (!ISO_DATE_RE.test(v)) return null;
  const [, m, d] = v.slice(0, 10).split('-');
  return `${Number(m)}.${Number(d)}까지`;
}

function daysBetween(fromISO: string, toISO: string): number {
  const a = Date.UTC(+fromISO.slice(0, 4), +fromISO.slice(5, 7) - 1, +fromISO.slice(8, 10));
  const b = Date.UTC(+toISO.slice(0, 4), +toISO.slice(5, 7) - 1, +toISO.slice(8, 10));
  return Math.round((b - a) / 86400000);
}

const EMPTY_VIEW: BenefitView = { items: [], untilDate: null, restricted: null };

/**
 * '혜택안내' 섹션 뷰 — 구조화 필드(kind/unit_type/할인율) 기반, 혜택 1건 = 카드 1장.
 *   - discount_benefits 가 배열이 아니면(회원 전용 티저) restricted 만 채운다
 *   - 정렬: 대표혜택(headlineId) → 분양가 인하(아파트→공통→오피스텔→상가) → 나머지(API 순서)
 *   - untilDate: 표시 혜택 중 가장 이른 종료일(섹션 머리 배지). 카드별 종료일은 그와 다를 때만
 *   - 출처 URL 은 API 에 없으며 여기서도 만들지 않는다(외부 링크 금지)
 */
export function buildBenefitView(
  property: Property,
  today: string = todayLocalISO(),
  headlineId: number | null = property.benefit_headline?.benefit_id ?? null,
): BenefitView {
  const raw = property.discount_benefits;
  if (raw && !Array.isArray(raw)) {
    const count = typeof raw.count === 'number' ? raw.count : 0;
    return { ...EMPTY_VIEW, restricted: { count } };
  }
  const visible = (raw ?? []).filter((b) => isBenefitVisible(b, today));

  const rank = (b: DiscountBenefit): number => {
    if (headlineId != null && b.id === headlineId) return 0;
    if (b.kind === 'price_cut') return 1 + UNIT_ORDER.indexOf(unitKey(b));
    return 10;
  };
  // Array.prototype.sort 는 안정 정렬 — 같은 순위는 API 순서(서버 order) 유지
  const sorted = visible.slice().sort((a, b) => rank(a) - rank(b));

  const untilDates = sorted
    .map((b) => (b.valid_until ?? '').trim().slice(0, 10))
    .filter((v) => ISO_DATE_RE.test(v))
    .sort();
  const untilDate = untilDates.length ? untilDates[0] : null;

  const mixedUnits = new Set(sorted.map(unitKey)).size >= 2;

  const items = sorted
    .map((b): BenefitItem | null => {
      const title = sanitizeContentText(b.title ?? '');
      const description = (b.description ?? '').trim();
      if (!title && !description) return null;
      const iso = (b.valid_until ?? '').trim().slice(0, 10);
      const own = ISO_DATE_RE.test(iso) && iso !== untilDate ? untilLabel(iso) : null;
      const d = own ? daysBetween(today, iso) : -1;
      const unit = unitKey(b);
      return {
        id: b.id,
        title,
        description,
        kind: b.kind ?? 'etc',
        kindLabel: KIND_LABEL[b.kind ?? ''],
        unitLabel: (mixedUnits || b.kind === 'price_cut') && unit ? UNIT_LABEL[unit] : null,
        valueBadge: valueBadgeOf(b),
        until: own,
        untilSoon: own ? d >= 0 && d <= 7 : false,
      };
    })
    .filter((i): i is BenefitItem => !!i);

  if (items.length === 0) return { ...EMPTY_VIEW };
  return { items, untilDate, restricted: null };
}

/** 평면도 이미지 — 타입명 + 이미지 URL. */
export interface FloorPlanImage {
  id: number;
  typeName: string;
  imageUrl: string;
}

/**
 * 상대경로 이미지 URL 을 안전하게 절대/루트 경로로 보정한다.
 * (기존 상세페이지 buildImageUrl 과 동일 규칙: http/절대경로면 그대로, 아니면 /media/ 접두)
 */
function resolveMediaUrl(raw?: string | null): string | null {
  if (!raw) return null;
  if (raw.startsWith('http') || raw.startsWith('/')) return raw;
  return `/media/${raw}`;
}

/**
 * 평면도 이미지 목록 — image_url 이 있는 floor_plans 만 반환한다.
 * 없으면 빈 배열.
 */
export function buildFloorPlanImages(property: Property): FloorPlanImage[] {
  const plans = property.floor_plans;
  if (!plans || plans.length === 0) return [];
  const images: FloorPlanImage[] = [];
  for (const fp of plans) {
    const url = resolveMediaUrl(fp.image_url);
    if (!url) continue;
    images.push({
      id: fp.id,
      typeName: cleanFieldText(fp.type_name) || fp.type_name || '',
      imageUrl: url,
    });
  }
  return images;
}

/** 요약 섹션 데이터. 가격은 policy formatter 경유. */
export function summaryData(property: Property): SummaryData {
  return {
    status: property.status,
    isPreRegistration: !!property.is_pre_registration,
    isReopen: !!property.is_reopen,
    priceLabel: formatPriceForDisplay(property.min_price, property.max_price),
    address: property.address,
    title: property.title,
  };
}
